import { PlusOutlined } from '@ant-design/icons';
import { type RelationEditor } from '@framework/form/form/relations/useRelationEditor.ts';
import { AdvancedSearchSelectInput } from '@framework/form/inputs/advanced-search/AdvancedSearchSelectInput.tsx';
import { Space } from 'antd';
import { type ColumnType } from 'antd/es/table';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AdvancedSearchFormItem } from '../../../../search/component/AdvancedSearchFormItem.tsx';
import { type IPickableColumn } from '../../IPickableColumn.tsx';

export interface IAddExtraColumnButtonProps<T> {
  editor: RelationEditor<IPickableColumn>;
  extraColumns?: Array<ColumnType<T>>;
}

/**
 * Pulsante che permette di aggiungere un nuovo tag semantico come colonna della tabella
 */
export function AddExtraColumnButton<T>(props: IAddExtraColumnButtonProps<T>) {
  const { editor, extraColumns } = props;

  const { t } = useTranslation();
  const label = t(
    'column-picker.add-extra-column-button.label',
    'Altri campi specializzati',
  );

  const items = useMemo(() => {
    const result =
      extraColumns?.map(column => ({
        key: column.key?.toString() ?? '<undefined>',
        label: column.title?.toLocaleString() ?? '<undefined>',
        disabled: editor.items.some(item => item.key === column.key),
      })) ?? [];
    return result;
  }, [editor.items, extraColumns]);

  if (items.length === 0) return null;

  return (
    <AdvancedSearchFormItem name={`__add_extra_column`} label={label}>
      <AdvancedSearchSelectInput
        placeholder={
          <Space>
            <PlusOutlined aria-hidden="true" />
            <span>{label}</span>
          </Space>
        }
        options={items}
        name={'__add_extra_column'}
        searchable
        skipResetOnMissingValue
        size="middle"
        onChange={(value, option, onChange) => {
          if (editor.items.some(item => item.key === value)) return;
          editor.append({
            key: value,
            id: value,
            index: editor.items.length,
            visible: true,
            fixed: false,
            showInExpandedRow: false,
          });

          onChange(null);

          setTimeout(() => {
            const element: HTMLInputElement | null = document.querySelector(
              `[data-searchid="${value}"] input:first-of-type`,
            );
            if (element != null && 'focus' in element) {
              element.focus();
            }
          }, 100);
        }}
      />
    </AdvancedSearchFormItem>
  );
}
